// components/Can.jsx
import React, { useContext, cloneElement } from 'react';
import { Tooltip } from 'antd';
import { AuthContext } from '../common/constants';

// Rôles ayant accès complet
const FULL_ACCESS_ROLES = ['superAdmin', 'directeur'];

// Vérifie qu'un utilisateur a la permission requise.
// Hook utilitaire réutilisable (Can lui-même, et appels custom).
export const useHasPermission = (perm, anyOf = []) => {
  const { currentUser } = useContext(AuthContext);
  if (FULL_ACCESS_ROLES.includes(currentUser?.role)) return true;
  const perms = new Set(currentUser?.permissions || []);
  const okOne = anyOf.length ? anyOf.some((p) => perms.has(p)) : true;
  const okSingle = perm ? perms.has(perm) : true;
  return okOne && okSingle;
};

// Composant Can : affiche children si la permission est OK.
//
// Modes :
//   - default (hidden)        : children retourné si autorisé, null sinon
//   - mode="disable"          : children toujours rendu, mais désactivé + tooltip explicatif si pas autorisé
//                                (utile pour discoverability — l'utilisateur sait que l'action existe)
//
// Usage :
//   <Can perm="clients:create"><Button>Nouveau</Button></Can>          // hidden si pas perm
//   <Can perm="clients:delete" mode="disable"><Button>...</Button></Can>  // disabled+tooltip si pas perm
//
export default function Can({ perm, anyOf = [], children, mode = 'hidden', deniedMessage }) {
  const allowed = useHasPermission(perm, anyOf);

  if (allowed) return children;

  if (mode === 'disable' && React.isValidElement(children)) {
    const tooltipMsg = deniedMessage || "Vous n'avez pas la permission d'effectuer cette action";
    return (
      <Tooltip title={tooltipMsg}>
        <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
          {cloneElement(children, {
            disabled: true,
            // Évite que le click se propage si Tooltip wrap un Button disabled
            onClick: (e) => e?.preventDefault?.(),
          })}
        </span>
      </Tooltip>
    );
  }

  return null;
}
